import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  CheckCircle, ShoppingCart, AlertTriangle, Truck,
  MapPin, Zap, Lock, ArrowRight, ClipboardList,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ordersAPI, productsAPI, myOrdersAPI } from '../api';

const MIN_ORDER = 5000;
const BULK_TIERS = [
  { threshold: 50000, label: '50K BULK DISCOUNT', desc: 'Contact us for pricing' },
  { threshold: 25000, label: '25K BULK DISCOUNT', desc: 'Contact us for pricing' },
];

// Badge colours by badge type
const BADGE_COLORS = {
  news:     { bg: '#1a1a2e', border: '#3a3a6e', text: '#8888ff', label: 'NEWS'     },
  restock:  { bg: '#0a1f0a', border: '#1a4a1a', text: '#22c55e', label: 'RESTOCK'  },
  discount: { bg: '#1f1500', border: '#4a3200', text: '#F5A800', label: 'DISCOUNT' },
  storm:    { bg: '#1f0a0a', border: '#4a1a1a', text: '#ef4444', label: 'STORM SALE'},
  alert:    { bg: '#1a1000', border: '#3a2800', text: '#F5A800', label: 'ALERT'    },
};

/* ─── Product row (standard) ──────────────────────────────────────────────── */
function ProductRow({ product, qty, onChange }) {
  const isLow = product.quantity > 0 && product.quantity <= product.low_stock_threshold;
  const isOut = product.quantity === 0;
  const active = qty > 0;
  const subtotal = qty * parseFloat(product.price);

  return (
    <div style={{
      background: active ? 'var(--bce-bg-elevated)' : 'var(--bce-bg-card)',
      padding: '1rem 1.5rem',
      display: 'flex', alignItems: 'center', gap: '1rem',
      borderLeft: `3px solid ${active ? 'var(--bce-gold)' : 'transparent'}`,
      transition: 'all 0.15s',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--font-condensed)', fontWeight: 700,
          fontSize: 14, letterSpacing: '0.08em', textTransform: 'uppercase',
          color: 'var(--bce-white)',
        }}>
          {product.name}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'var(--bce-gold)' }}>
            ${parseFloat(product.price).toLocaleString()}
          </span>
          <span style={{ fontSize: 11, color: 'var(--bce-text-faint)' }}>/ {product.unit}</span>
          {isLow && !isOut && (
            <span style={{
              fontSize: 9, fontFamily: 'var(--font-condensed)', fontWeight: 700,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              background: 'rgba(245,168,0,0.12)', color: 'var(--bce-gold)',
              border: '1px solid rgba(245,168,0,0.25)',
              padding: '1px 6px',
            }}>
              ⚠ LOW STOCK
            </span>
          )}
          {isOut && (
            <span style={{
              fontSize: 9, fontFamily: 'var(--font-condensed)', fontWeight: 700,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              background: 'rgba(239,68,68,0.1)', color: 'var(--bce-danger)',
              border: '1px solid rgba(239,68,68,0.25)',
              padding: '1px 6px',
            }}>
              OUT OF STOCK
            </span>
          )}
        </div>
        {(isLow || isOut) && (
          <div style={{ fontSize: 11, color: 'var(--bce-text-faint)', marginTop: 3 }}>
            {isOut
              ? 'We still accept orders — expect longer lead times'
              : `Only ${product.quantity} units left — may take longer`}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--bce-text-faint)' }}>Qty</span>
        <select
          className="bce-select"
          value={qty}
          onChange={e => onChange(parseInt(e.target.value))}
          style={{ width: 80, padding: '8px 28px 8px 10px' }}
        >
          {[0,1,2,3,4,5,10,15,20,25,50,75,100,150,200,250,300,400,500,750,1000,1250,1500].map(q => (
            <option key={q} value={q}>{q}</option>
          ))}
        </select>
      </div>

      {active && (
        <div style={{
          fontFamily: 'var(--font-display)', fontSize: '1.2rem',
          color: 'var(--bce-gold)', minWidth: 90, textAlign: 'right',
        }}>
          ${subtotal.toLocaleString()}
        </div>
      )}
    </div>
  );
}

/* ─── Limited item row ────────────────────────────────────────────────────── */
function LimitedRow({ product, qty, onChange, alreadyOwned }) {
  const active = qty > 0;
  const isOut = product.quantity === 0;
  const blocked = alreadyOwned;

  return (
    <motion.div
      whileHover={{ x: blocked ? 0 : 3 }}
      style={{
        background: blocked ? 'rgba(30,20,0,0.4)' : active ? '#1a1200' : 'var(--bce-bg-card)',
        border: `1px solid ${blocked ? 'rgba(245,168,0,0.08)' : active ? 'rgba(245,168,0,0.4)' : 'rgba(245,168,0,0.12)'}`,
        padding: '1.25rem 1.5rem',
        display: 'flex', alignItems: 'center', gap: '1rem',
        transition: 'all 0.18s',
        position: 'relative', overflow: 'hidden',
        opacity: blocked ? 0.55 : 1,
      }}
    >
      {/* Gold shimmer bar on active */}
      {active && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          height: 2, background: 'var(--bce-gold)',
        }} />
      )}

      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{
            fontFamily: 'var(--font-condensed)', fontWeight: 700,
            fontSize: 14, letterSpacing: '0.08em', textTransform: 'uppercase',
            color: 'var(--bce-white)',
          }}>
            {product.name}
          </span>
          <span style={{
            fontSize: 8, fontFamily: 'var(--font-condensed)', fontWeight: 700,
            letterSpacing: '0.15em', textTransform: 'uppercase',
            background: 'var(--bce-gold)', color: '#000',
            padding: '2px 7px',
          }}>
            LIMITED
          </span>
          {blocked && (
            <span style={{
              fontSize: 8, fontFamily: 'var(--font-condensed)', fontWeight: 700,
              letterSpacing: '0.15em', textTransform: 'uppercase',
              background: 'rgba(239,68,68,0.12)', color: 'var(--bce-danger)',
              border: '1px solid rgba(239,68,68,0.25)',
              padding: '2px 7px',
            }}>
              ALREADY OWNED
            </span>
          )}
          <span style={{
            fontSize: 9, fontFamily: 'var(--font-condensed)',
            color: 'var(--bce-text-faint)', letterSpacing: '0.1em',
          }}>
            {product.quantity} / 90 remaining
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--bce-gold)' }}>
            ${parseFloat(product.price).toLocaleString()}
          </span>
          <span style={{ fontSize: 11, color: 'var(--bce-text-faint)' }}>1 per person</span>
        </div>
      </div>

      {/* Stock bar */}
      <div style={{ width: 60, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
        <div style={{ width: '100%', height: 4, background: 'var(--bce-border)', position: 'relative' }}>
          <div style={{
            position: 'absolute', top: 0, left: 0,
            height: '100%',
            width: `${Math.min(100, (product.quantity / 90) * 100)}%`,
            background: product.quantity > 30
              ? 'var(--bce-success)'
              : product.quantity > 10 ? 'var(--bce-gold)' : 'var(--bce-danger)',
            transition: 'width 0.3s',
          }} />
        </div>
        <span style={{ fontSize: 9, color: 'var(--bce-text-faint)', fontFamily: 'var(--font-condensed)', letterSpacing: '0.05em' }}>
          STOCK
        </span>
      </div>

      {/* Toggle button */}
      <button
        onClick={() => !blocked && onChange(active ? 0 : 1)}
        disabled={isOut || blocked}
        style={{
          padding: '8px 18px',
          background: blocked ? 'transparent' : active ? 'var(--bce-gold)' : 'transparent',
          border: `1px solid ${blocked ? 'rgba(239,68,68,0.25)' : active ? 'var(--bce-gold)' : 'rgba(245,168,0,0.3)'}`,
          color: blocked ? 'var(--bce-danger)' : active ? '#000' : 'var(--bce-gold)',
          fontFamily: 'var(--font-condensed)', fontSize: 12,
          fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
          cursor: (isOut || blocked) ? 'not-allowed' : 'pointer',
          transition: 'all 0.15s',
          opacity: isOut ? 0.4 : 1,
          display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        {blocked ? <><Lock size={11} /> Owned</> : active ? <><Lock size={11} /> Added</> : 'Add'}
      </button>
    </motion.div>
  );
}

/* ─── Delivery option ─────────────────────────────────────────────────────── */
function DeliveryOption({ value, label, sublabel, icon: Icon, selected, onSelect }) {
  return (
    <div
      onClick={onSelect}
      style={{
        flex: 1, padding: '1.25rem 1.5rem',
        border: `1px solid ${selected ? 'var(--bce-gold)' : 'var(--bce-border)'}`,
        background: selected ? '#1a1200' : 'var(--bce-bg-card)',
        cursor: 'pointer', transition: 'all 0.15s',
        display: 'flex', alignItems: 'flex-start', gap: 12,
        position: 'relative',
      }}
    >
      {selected && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'var(--bce-gold)' }} />
      )}
      <Icon size={20} color={selected ? 'var(--bce-gold)' : 'var(--bce-text-faint)'} style={{ marginTop: 2, flexShrink: 0 }} />
      <div>
        <div style={{
          fontFamily: 'var(--font-condensed)', fontWeight: 700,
          fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase',
          color: selected ? 'var(--bce-white)' : 'var(--bce-text)',
          marginBottom: 3,
        }}>
          {label}
        </div>
        <div style={{ fontSize: 12, color: 'var(--bce-text-faint)', lineHeight: 1.5 }}>
          {sublabel}
        </div>
      </div>
      <div style={{
        marginLeft: 'auto', width: 16, height: 16, flexShrink: 0,
        border: `2px solid ${selected ? 'var(--bce-gold)' : 'var(--bce-border)'}`,
        borderRadius: '50%', marginTop: 2,
        background: selected ? 'var(--bce-gold)' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {selected && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#000' }} />}
      </div>
    </div>
  );
}

/* ─── Main order form ─────────────────────────────────────────────────────── */
export default function OrderForm() {
  const navigate = useNavigate();
  const [submitted, setSubmitted]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [products, setProducts]     = useState([]);
  const [loadingProducts, setLoadingP] = useState(true);
  const [name, setName]             = useState('');
  const [phone, setPhone]           = useState('');
  const [notes, setNotes]           = useState('');
  const [delivery, setDelivery]     = useState('pickup');
  const [qtys, setQtys]             = useState({});
  // Track which limited items this user already owns (from existing orders)
  const [ownedLimited, setOwnedLimited] = useState({});
  const isLoggedIn = !!localStorage.getItem('bce_token');

  // Load products + check existing limited purchases if logged in
  useEffect(() => {
    productsAPI.list()
      .then(r => {
        setProducts(r.data);
        setQtys(Object.fromEntries(r.data.map(p => [p.name, 0])));
      })
      .catch(() => toast.error('Failed to load products'))
      .finally(() => setLoadingP(false));

    if (isLoggedIn) {
      myOrdersAPI.list()
        .then(r => {
          // Build a map of limited item name → total quantity already ordered (non-cancelled)
          const owned = {};
          (r.data || []).forEach(order => {
            if (order.status === 'cancelled') return;
            (order.items || []).forEach(item => {
              owned[item.name] = (owned[item.name] || 0) + item.quantity;
            });
          });
          setOwnedLimited(owned);
        })
        .catch(() => {}); // silent — not critical
    }
  }, [isLoggedIn]);

  const setQty = (name, qty) => setQtys(q => ({ ...q, [name]: qty }));

  const regularProducts = products.filter(p => !p.is_limited);
  const limitedProducts = products.filter(p => p.is_limited);

  const orderItems = useMemo(() =>
    products
      .filter(p => qtys[p.name] > 0)
      .map(p => ({
        name:     p.name,
        quantity: qtys[p.name],
        price:    parseFloat(p.price),
      })),
    [products, qtys]
  );

  const total = useMemo(() =>
    orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [orderItems]
  );

  const remaining = MIN_ORDER - total;
  const meetsMin  = total >= MIN_ORDER;

  // Bulk tier — which tier is approaching or reached
  const bulkTier = BULK_TIERS.find(t => total >= t.threshold);
  const nextTier = !bulkTier ? BULK_TIERS.find(t => total >= t.threshold * 0.6) : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim())  { toast.error('Please enter your name'); return; }
    if (!phone.trim()) { toast.error('Please enter your phone number'); return; }
    if (orderItems.length === 0) { toast.error('Select at least one item'); return; }
    if (!meetsMin) {
      toast.error(`Minimum order is $${MIN_ORDER.toLocaleString()}. Add $${remaining.toLocaleString()} more.`);
      return;
    }
    setSubmitting(true);
    try {
      await ordersAPI.submit({
        customer_name:  name,
        customer_phone: phone,
        items:          orderItems,
        delivery_type:  delivery,
        notes,
        // discord_id resolved server-side from JWT if logged in
      });
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to submit order. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success screen ─────────────────────────────────────────────────────────
  if (submitted) return (
    <div className="bce-page" style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bce-bg)', padding: '2rem',
    }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        style={{
          textAlign: 'center', padding: '3.5rem 3rem',
          background: 'var(--bce-bg-card)',
          border: '1px solid var(--bce-border)',
          borderTop: '3px solid var(--bce-gold)',
          maxWidth: 520, width: '100%',
        }}
      >
        <motion.div
          initial={{ scale: 0 }} animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        >
          <CheckCircle size={52} color="var(--bce-gold)" style={{ margin: '0 auto 1.5rem' }} />
        </motion.div>
        <div style={{
          fontFamily: 'var(--font-condensed)', fontSize: 10,
          letterSpacing: '0.25em', color: 'rgba(245,168,0,0.5)',
          textTransform: 'uppercase', marginBottom: 8,
        }}>
          Big Country Energy
        </div>
        <h2 className="bce-display" style={{ fontSize: '3rem', marginBottom: '1rem' }}>
          ORDER RECEIVED
        </h2>
        <p style={{ color: 'var(--bce-text)', lineHeight: 1.8, marginBottom: '0.5rem' }}>
          Thanks <strong style={{ color: 'var(--bce-white)' }}>{name}</strong>!
          Your order has been logged and our team has been notified.
        </p>
        <p style={{ color: 'var(--bce-text)', lineHeight: 1.8, marginBottom: '2rem', fontSize: 14 }}>
          We'll reach you at{' '}
          <strong style={{ color: 'var(--bce-white)' }}>{phone}</strong>{' '}
          to confirm details and arrange{' '}
          {delivery === 'delivery' ? 'GTY delivery' : 'pickup'}.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="bce-btn bce-btn-gold" onClick={() => {
            setSubmitted(false);
            setQtys(Object.fromEntries(products.map(p => [p.name, 0])));
            setName(''); setPhone(''); setNotes(''); setDelivery('pickup');
          }}>
            Place Another Order <ArrowRight size={14} />
          </button>
          {isLoggedIn && (
            <button className="bce-btn bce-btn-ghost" onClick={() => navigate('/my-orders')}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <ClipboardList size={14} /> My Orders
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );

  // ── Main form ──────────────────────────────────────────────────────────────
  return (
    <div className="bce-page" style={{ background: 'var(--bce-bg)', paddingBottom: '5rem' }}>
      <div className="bce-container-sm" style={{ paddingTop: '4rem' }}>

        {/* Page header */}
        <motion.div
          initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
          style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}
        >
          <div style={{ width: 30, height: 2, background: 'var(--bce-gold)' }} />
          <span className="bce-label">Purchase Order</span>
        </motion.div>

        <div style={{ overflow: 'hidden', marginBottom: '0.5rem' }}>
          <motion.h1
            className="bce-display"
            initial={{ y: '100%' }} animate={{ y: 0 }}
            transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
            style={{ fontSize: 'clamp(3rem, 8vw, 5.5rem)', color: 'var(--bce-white)' }}
          >
            PLACE AN ORDER
          </motion.h1>
        </div>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
          style={{ color: 'var(--bce-text)', lineHeight: 1.8, marginBottom: '3rem', fontSize: 14 }}
        >
          Can't reach us right now? Submit a pre-order and we'll contact you to confirm.
          Minimum order value: <strong style={{ color: 'var(--bce-gold)' }}>${MIN_ORDER.toLocaleString()}</strong>.
        </motion.p>

        {loadingProducts ? (
          <div className="bce-loading"><div className="bce-spinner" /> Loading products...</div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>

            {/* ── Contact info ─────────────────────────────────────────────── */}
            <div>
              <div className="bce-section-label" style={{ marginBottom: '1rem' }}><span>Your Info</span></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="bce-field">
                  <label>In-Game Name *</label>
                  <input className="bce-input" value={name} onChange={e => setName(e.target.value)} placeholder="Your character name" />
                </div>
                <div className="bce-field">
                  <label>Phone Number *</label>
                  <input className="bce-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="In-game phone" />
                </div>
              </div>
            </div>

            {/* ── Delivery ─────────────────────────────────────────────────── */}
            <div>
              <div className="bce-section-label" style={{ marginBottom: '1rem' }}><span>Delivery Method</span></div>
              <div style={{ display: 'flex', gap: '1px', background: 'var(--bce-border)' }}>
                <DeliveryOption
                  value="pickup"
                  label="Pickup"
                  sublabel="Come to our location. Fastest option."
                  icon={MapPin}
                  selected={delivery === 'pickup'}
                  onSelect={() => setDelivery('pickup')}
                />
                <DeliveryOption
                  value="delivery"
                  label="GTY Delivery"
                  sublabel="We deliver to you via our GTY partnership. Larger orders recommended."
                  icon={Truck}
                  selected={delivery === 'delivery'}
                  onSelect={() => setDelivery('delivery')}
                />
              </div>
            </div>

            {/* ── Regular products ──────────────────────────────────────────── */}
            <div>
              <div className="bce-section-label" style={{ marginBottom: '1rem' }}><span>Select Products</span></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: 'var(--bce-border)' }}>
                {regularProducts.map(p => (
                  <ProductRow
                    key={p.name}
                    product={p}
                    qty={qtys[p.name] || 0}
                    onChange={qty => setQty(p.name, qty)}
                  />
                ))}
              </div>
            </div>

            {/* ── Limited items ─────────────────────────────────────────────── */}
            {limitedProducts.length > 0 && (
              <div>
                <div className="bce-section-label" style={{ marginBottom: '0.5rem' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Zap size={11} color="var(--bce-gold)" />
                    Limited Items
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <div style={{ fontSize: 11, color: 'var(--bce-text-faint)' }}>
                    1 per person · requires Discord login to enforce
                  </div>
                  {isLoggedIn && (
                    <button
                      type="button"
                      onClick={() => navigate('/my-orders')}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: 11, color: 'var(--bce-gold)',
                        fontFamily: 'var(--font-condensed)', letterSpacing: '0.1em',
                        textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      <ClipboardList size={11} /> View My Orders
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {limitedProducts.map(p => (
                    <LimitedRow
                      key={p.name}
                      product={p}
                      qty={qtys[p.name] || 0}
                      onChange={qty => setQty(p.name, qty)}
                      alreadyOwned={isLoggedIn && (ownedLimited[p.name] || 0) >= (p.max_per_order || 1)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* ── Notes ────────────────────────────────────────────────────── */}
            <div className="bce-field">
              <label>Additional Notes</label>
              <textarea
                className="bce-textarea" rows={3} value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Any special requests, gang affiliation for discount eligibility, etc."
                style={{ minHeight: 80 }}
              />
            </div>

            {/* ── Bulk discount teaser ──────────────────────────────────────── */}
            <AnimatePresence>
              {total >= 15000 && (
                <motion.div
                  key="bulk"
                  initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                  style={{
                    padding: '1rem 1.5rem',
                    background: bulkTier ? 'rgba(245,168,0,0.08)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${bulkTier ? 'rgba(245,168,0,0.35)' : 'rgba(245,168,0,0.15)'}`,
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}
                >
                  <Zap size={16} color="var(--bce-gold)" style={{ flexShrink: 0 }} />
                  <div>
                    {bulkTier ? (
                      <>
                        <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--bce-gold)', textTransform: 'uppercase' }}>
                          🎉 {bulkTier.label} ELIGIBLE
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--bce-text)', marginTop: 2 }}>
                          Mention this when we contact you. {bulkTier.desc}.
                        </div>
                      </>
                    ) : nextTier ? (
                      <>
                        <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--bce-white)', textTransform: 'uppercase' }}>
                          ${(nextTier.threshold - total).toLocaleString()} away from {nextTier.label}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--bce-text)', marginTop: 2 }}>
                          Reach ${nextTier.threshold.toLocaleString()} for bulk discount pricing.
                        </div>
                      </>
                    ) : null}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── Total + submit ────────────────────────────────────────────── */}
            <div style={{
              background: 'var(--bce-bg-card)',
              border: '1px solid var(--bce-border)',
              borderTop: `3px solid ${meetsMin ? 'var(--bce-gold)' : 'var(--bce-danger)'}`,
              padding: '1.5rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                <div>
                  <div className="bce-label" style={{ marginBottom: 4 }}>Order Total</div>
                  <div style={{
                    fontFamily: 'var(--font-display)', fontSize: '2.5rem',
                    color: total === 0 ? 'var(--bce-text-faint)' : meetsMin ? 'var(--bce-gold)' : 'var(--bce-danger)',
                    letterSpacing: '0.02em', lineHeight: 1,
                  }}>
                    ${total.toLocaleString()}
                  </div>

                  {/* Progress bar toward minimum */}
                  {total > 0 && !meetsMin && (
                    <div style={{ marginTop: 8, width: 200 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 10, color: 'var(--bce-danger)', fontFamily: 'var(--font-condensed)', letterSpacing: '0.08em' }}>
                          MINIMUM NOT MET
                        </span>
                        <span style={{ fontSize: 10, color: 'var(--bce-text-faint)' }}>
                          ${remaining.toLocaleString()} more
                        </span>
                      </div>
                      <div style={{ height: 3, background: 'var(--bce-border)', position: 'relative' }}>
                        <div style={{
                          position: 'absolute', top: 0, left: 0,
                          height: '100%',
                          width: `${Math.min(100, (total / MIN_ORDER) * 100)}%`,
                          background: 'var(--bce-danger)',
                          transition: 'width 0.3s',
                        }} />
                      </div>
                    </div>
                  )}

                  {meetsMin && orderItems.length > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--bce-text-faint)', marginTop: 4 }}>
                      {orderItems.length} product{orderItems.length > 1 ? 's' : ''} · {delivery === 'delivery' ? 'GTY Delivery' : 'Pickup'}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                  {!meetsMin && total > 0 && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      fontSize: 12, color: 'var(--bce-danger)',
                      fontFamily: 'var(--font-condensed)', letterSpacing: '0.06em',
                    }}>
                      <AlertTriangle size={12} />
                      Minimum: ${MIN_ORDER.toLocaleString()}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="bce-btn bce-btn-gold"
                    disabled={submitting || orderItems.length === 0 || !meetsMin}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      opacity: (orderItems.length === 0 || !meetsMin) ? 0.45 : 1,
                    }}
                  >
                    <ShoppingCart size={16} />
                    {submitting ? 'Submitting...' : 'Submit Order'}
                  </button>
                </div>
              </div>
            </div>

          </form>
        )}
      </div>
    </div>
  );
}
